import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import itCommon from "./locales/it/common.json";

/**
 * Mytharion i18n — FASE 1
 *
 * Central i18next configuration.
 *
 * Detection priority:
 *   1. Explicit user preference (localStorage `mytharion_lang` — hydrated
 *      from the authenticated user's `language` field by `useLanguageSync`).
 *   2. HTML <html lang="..."> attribute (SSR / prerender hint).
 *   3. Browser `navigator.language`.
 *   4. Fallback: English.
 *
 * Supported languages: en, it.  Fallback: en (primary product language and
 * primary SEO target).  Adding a new language later requires only:
 *   - creating /app/frontend/src/i18n/locales/<code>/common.json,
 *   - importing + registering it in `resources` below,
 *   - adding <code> to `SUPPORTED_LANGUAGES`.
 */

export const SUPPORTED_LANGUAGES = ["en", "it"];
export const DEFAULT_LANGUAGE = "en";
export const LANGUAGE_STORAGE_KEY = "mytharion_lang";

const resources = {
  en: { common: enCommon },
  it: { common: itCommon },
};

// Guard: never let i18next call `init` twice in dev under HMR/StrictMode.
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: "common",
      ns: ["common"],
      interpolation: { escapeValue: false },
      detection: {
        order: ["localStorage", "htmlTag", "navigator"],
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ["localStorage"],
      },
      returnEmptyString: false,
      react: { useSuspense: false },
    });
}

// Keep <html lang="..."> in sync so screen readers, Google, and Facebook
// scrapers see the correct language for the currently rendered UI.
const syncHtmlLang = (lng) => {
  if (typeof document === "undefined") return;
  const two = (lng || DEFAULT_LANGUAGE).split("-")[0];
  const supported = SUPPORTED_LANGUAGES.includes(two) ? two : DEFAULT_LANGUAGE;
  document.documentElement.setAttribute("lang", supported);
};

syncHtmlLang(i18n.language);
i18n.on("languageChanged", syncHtmlLang);

export default i18n;
